import { useEffect, useState } from 'react';
import { Heading } from './Heading';
import { SlideMedia } from './SlideMedia';

/**
 * Hero con slideshow di media in background (crossfade) + testo sopra.
 * A differenza di HeroBlock il testo è sempre chiaro (sfondo foto/video) —
 * se lo usi come primo blocco ricorda di impostare `dark` su <SiteHeader>
 * in Header.jsx.
 *
 * Il testo (eyebrow/titolo/sottotitolo/CTA) è condiviso da tutte le slide
 * (resta l'H1 di pagina, non ha senso farlo cambiare a ogni crossfade) —
 * `showOverlay` è per-slide e decide solo se, mentre QUELLA slide è attiva,
 * lo scrim scuro e il testo sono visibili o no: permette di alternare slide
 * "con didascalia" a momenti puramente visivi (es. un video ambient pulito).
 *
 * Ogni slide video è montata sempre (autoplay silenzioso, opacità a 0 se non
 * attiva) esattamente come le immagini, per lo stesso identico crossfade —
 * non c'è un modo di "pre-caricare senza riprodurre" un embed YouTube/Vimeo,
 * quindi con più slide video il costo di banda/CPU è più alto che con sole
 * immagini: accettabile per il numero di slide tipico di un hero (2-4).
 *
 * @param {{
 *   slides?: Array<{ mediaType?: 'image'|'video', image?: string, imageAlt?: string, videoUrl?: string, showOverlay?: boolean }>,
 *   interval?: number, autoplay?: boolean,
 *   eyebrow?: string, title?: string, subtitle?: string, cta?: string, ctaHref?: string,
 *   headingLevel?: number,
 * }} props
 */
export function HeroSlideshowBlock({
  slides = [], interval = 5000, autoplay = true,
  eyebrow, title, subtitle, cta, ctaHref = '#', headingLevel = 1,
}) {
  const [active, setActive] = useState(0);
  const activeSlide = slides[active];
  const showOverlay = activeSlide?.showOverlay !== false;

  useEffect(() => {
    if (!autoplay || slides.length < 2) return;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), Math.max(1000, interval));
    return () => clearInterval(id);
  }, [autoplay, interval, slides.length]);

  return (
    <section className="relative min-h-screen flex items-center px-6 md:px-16 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-theme-bg-subtle">
        {slides.map((slide, i) => {
          // Solo la prima slide è visibile subito: se questo blocco è anche
          // il primo della pagina (headingLevel 1) è probabile immagine LCP
          // (solo per un'immagine, non per un video incorporato).
          const isFirstSlide = i === 0;
          const isLikelyLcp = isFirstSlide && headingLevel === 1;
          return (
            <div key={i} className="absolute inset-0 transition-opacity duration-1000 ease-in-out" style={{ opacity: i === active ? 1 : 0 }}>
              <SlideMedia
                mediaType={slide.mediaType}
                image={slide.image}
                imageAlt={slide.imageAlt}
                videoUrl={slide.videoUrl}
                fallbackAlt={title}
                loading={isFirstSlide ? 'eager' : 'lazy'}
                fetchpriority={isLikelyLcp ? 'high' : 'auto'}
              />
            </div>
          );
        })}
        {showOverlay && <div className="absolute inset-0 bg-black/35" />}
      </div>

      {showOverlay && (
        <div className="max-w-3xl">
          {eyebrow && <p className="text-white/70 text-xs tracking-[0.14em] uppercase mb-6">{eyebrow}</p>}
          {title && (
            <Heading level={headingLevel} className="font-heading text-display font-light tracking-tight leading-[1.05] mb-8 text-white whitespace-pre-line">
              {title}
            </Heading>
          )}
          {subtitle && <p className="text-body-lg text-white/85 max-w-xl leading-relaxed mb-10">{subtitle}</p>}
          {cta && (
            <a href={ctaHref} className="inline-flex items-center px-7 py-3 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide">
              {cta}
            </a>
          )}
        </div>
      )}

      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex">
          {slides.map((_, i) => (
            // Pallino visibile piccolo (w-1.5), ma l'area cliccabile è il
            // bottone intero (p-2.5) — altrimenti il target da toccare è
            // troppo piccolo su mobile (violazione "touch target size"
            // trovata con Lighthouse: 6×6px, ben sotto i 24px minimi).
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Slide ${i + 1}`}
              className="flex items-center justify-center p-2.5"
            >
              <span
                className="block w-1.5 h-1.5 rounded-full transition-colors"
                style={{ background: i === active ? '#fff' : 'rgba(255,255,255,0.4)' }}
              />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
